import { Chat, ChatHeader, ChatMessageInputBar, ContactInfoCard } from '@if/mui'
import { ChatMessageInputBarHandle, ChatPageSkeletonLayout, MessageVariant } from '@if/mui'
import { ECreditApplicationStatus, ELanguage, EMessageType } from '@if/core/types'
import { Box, Container, useTheme } from '@mui/material'
import { blue, blueGrey, grey } from '@mui/material/colors'
import FullScreenContainer from '@src/components/FullScreenContainer'
import {
  useAcquireChatRoomLock,
  useDeleteMessage,
  useEditMessage,
  useForceAcquireChatRoomLock,
  useGetChatRoomLock,
  useGetConversation,
  useGetTemplate,
  useGetTemplates,
  useMarkConversationAsRead,
  useMarkMessageRead,
  useMarkMessageUnread,
  useReleaseChatRoomLock,
  releaseChatRoomLockOnUnload,
  useSendMessage,
  useGetPlaceholders,
} from '@src/data/api/chat/chat-api'
import { Constants, CURRENT_USER_PARTICIPANT_TYPE } from '@src/data/types/constants'
import {
  applyChatMessageEvent,
  getSignalRConnection,
  useSignalRConnectionReady,
  type MessageUpdatedEvent,
  MessageUpdateType,
} from '@src/data/api/chat-message/chat-message-signalr-api'
import { useUpdateLockCache } from '@src/data/api/chat/chat-api'
import type { ChatMessage, ChatRoomLock } from '@if/core/types'
import { useUser } from '@src/providers/UserProvider'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useFeatureFlags } from '@src/data/selectors/app-config-selectors'
import QuickActionsBar from './QuickActionsBar'
import { TEMPLATE_CATEGORIES } from './template-category-config'

const ChatPage = () => {
  const featureFlags = useFeatureFlags()

  const { creditApplicationId, financingProgramId } = useParams<{
    creditApplicationId: string
    financingProgramId: string
  }>()
  const location = useLocation()
  const navigate = useNavigate()
  const user = useUser()
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  const chatPalette = useMemo(
    () => ({
      background: isDark ? grey[900] : grey[100],
      messageSent: blue[700],
      messageSentText: '#FFFFFF',
      messageReceived: isDark ? blueGrey[800] : blue[50],
      messageReceivedText: isDark ? grey[100] : grey[900],
      avatarSent: isDark ? blue[500] : blue[700],
      avatarReceived: isDark ? blueGrey[600] : blue[200],
      isCancelled: isDark ? grey[600] : grey[300],
      messageNeutral: theme.palette.action.selected,
      messageNeutralText: theme.palette.text.primary,
    }),
    [isDark, theme],
  )

  const getMessageVariant = useCallback((message: ChatMessage): MessageVariant => {
    if (message.sentById === Constants.SystemUserId) return 'neutral'
    return message.type === EMessageType.Internal ? 'sent' : 'received'
  }, [])

  const applicationStatus = (location.state as { applicationStatus?: string } | null)?.applicationStatus
  const isCancelled =
    applicationStatus === ECreditApplicationStatus.Cancelled || applicationStatus === ECreditApplicationStatus.Expired

  const [formatMode, setFormatMode] = useState(true)
  const messageInputRef = useRef<ChatMessageInputBarHandle>(null)

  // Data fetching
  const [conversation, isLoading] = useGetConversation(creditApplicationId, financingProgramId)
  const [templates, isLoadingTemplates] = useGetTemplates(creditApplicationId, financingProgramId)
  const [placeholders] = useGetPlaceholders(creditApplicationId, financingProgramId)

  // Template fetching for QuickActionsBar
  const [templateCodeToFetch, setTemplateCodeToFetch] = useState<string | undefined>(undefined)
  const [fetchedTemplate, isFetchingTemplate, fetchError] = useGetTemplate(
    templateCodeToFetch,
    creditApplicationId,
    financingProgramId,
  )

  const handleFetchTemplate = useCallback((code: string) => {
    setTemplateCodeToFetch(code)
  }, [])

  // Lock management
  const [lock, isLoadingLock] = useGetChatRoomLock(creditApplicationId)
  const [acquireLock, acquireLockPending] = useAcquireChatRoomLock()
  const [releaseLock, releaseLockPending] = useReleaseChatRoomLock()
  const [forceAcquireLock, forceAcquireLockPending] = useForceAcquireChatRoomLock()

  const isLockedByOther = lock?.isActive && lock?.agentId !== user.id
  const isInputDisabled = !!isLockedByOther || isCancelled

  const handleAcquireLock = useCallback(() => {
    if (creditApplicationId && financingProgramId && conversation?.merchantId) {
      void acquireLock({ creditApplicationId, merchantId: conversation.merchantId, financingProgramId })
    }
  }, [creditApplicationId, financingProgramId, conversation?.merchantId, acquireLock])

  const handleReleaseLock = useCallback(() => {
    if (creditApplicationId) {
      void releaseLock(creditApplicationId)
    }
  }, [creditApplicationId, releaseLock])

  const handleForceAcquireLock = useCallback(() => {
    if (creditApplicationId && financingProgramId && conversation?.merchantId) {
      void forceAcquireLock({ creditApplicationId, merchantId: conversation.merchantId, financingProgramId })
    }
  }, [creditApplicationId, financingProgramId, conversation?.merchantId, forceAcquireLock])

  // Auto-acquire lock on mount if free
  const hasAttemptedAutoAcquire = useRef(false)
  useEffect(() => {
    if (
      creditApplicationId &&
      conversation?.merchantId &&
      !isLoadingLock &&
      lock !== undefined &&
      !hasAttemptedAutoAcquire.current &&
      !lock.isActive
    ) {
      hasAttemptedAutoAcquire.current = true
      handleAcquireLock()
    }
  }, [creditApplicationId, conversation?.merchantId, isLoadingLock, lock, handleAcquireLock])

  // Release lock on unmount
  const lockOwnershipRef = useRef<{ isOwner: boolean; creditApplicationId: string | null }>({
    isOwner: false,
    creditApplicationId: null,
  })
  const releaseLockRef = useRef(releaseLock)
  releaseLockRef.current = releaseLock

  useEffect(() => {
    lockOwnershipRef.current = {
      isOwner: lock?.isActive === true && lock?.agentId === user.id,
      creditApplicationId: creditApplicationId ?? null,
    }
  }, [lock?.isActive, lock?.agentId, user.id, creditApplicationId])

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (lockOwnershipRef.current.isOwner && lockOwnershipRef.current.creditApplicationId) {
        releaseChatRoomLockOnUnload(lockOwnershipRef.current.creditApplicationId)
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      if (lockOwnershipRef.current.isOwner && lockOwnershipRef.current.creditApplicationId) {
        void releaseLockRef.current(lockOwnershipRef.current.creditApplicationId)
      }
    }
  }, [])

  // Message mutations
  const [sendMsg] = useSendMessage()
  const [deleteMsg] = useDeleteMessage()
  const [editMsg] = useEditMessage()
  const [markUnread] = useMarkMessageUnread()
  const [markAsRead] = useMarkMessageRead()
  const [markConversationAsRead] = useMarkConversationAsRead()

  const [localMessages, setLocalMessages] = useState<ChatMessage[]>([])

  // SignalR real-time updates (lock + messages)
  const signalRReady = useSignalRConnectionReady()
  const updateLockCache = useUpdateLockCache()
  const lockRef = useRef(lock)
  lockRef.current = lock
  const markAsReadRef = useRef(markAsRead)
  markAsReadRef.current = markAsRead

  useEffect(() => {
    const connection = getSignalRConnection()
    if (!connection) return

    const handleLockChanged = (lockUpdate: ChatRoomLock) => {
      if (lockUpdate.creditApplicationId === creditApplicationId) {
        updateLockCache(lockUpdate)
      }
    }

    const handleMessageUpdated = (event: MessageUpdatedEvent) => {
      if (event.creditApplicationId !== creditApplicationId) return

      setLocalMessages((prev) => applyChatMessageEvent(prev, event))

      if (
        event.updateType === MessageUpdateType.Created &&
        lockRef.current?.isActive &&
        lockRef.current?.agentId === user.id &&
        event.updatedById &&
        event.updatedById !== user.id &&
        !user.roles.includes('Representative')
      ) {
        void markAsReadRef.current({ creditApplicationId: creditApplicationId ?? '', messageId: event.id })
      }
    }

    connection.on('lockChanged', handleLockChanged)
    connection.on('messageUpdated', handleMessageUpdated)

    return () => {
      connection.off('lockChanged', handleLockChanged)
      connection.off('messageUpdated', handleMessageUpdated)
    }
  }, [creditApplicationId, user.id, user.roles, updateLockCache, signalRReady])

  // Initialize local messages from server data
  useEffect(() => {
    if (conversation?.messages) {
      setLocalMessages(conversation.messages)

      const isOwnLock = lock?.isActive && lock?.agentId === user.id
      if (creditApplicationId && isOwnLock && conversation.messages.some((m) => !m.isRead && m.sentById !== user.id)) {
        void markConversationAsRead({ creditApplicationId }).then((response) => {
          if (response?.messageCount > 0) {
            setLocalMessages((prev) =>
              prev.map((m) =>
                !m.isRead && m.sentById !== user.id
                  ? { ...m, isRead: true, readAt: new Date(), readByName: user.name }
                  : m,
              ),
            )
          }
        })
      }
    }
  }, [conversation?.messages, creditApplicationId, user.id, user.name, markConversationAsRead, lock, setLocalMessages])

  const handleDeleteMessage = useCallback(
    async (messageId: string) => {
      if (isInputDisabled || !creditApplicationId) return
      await deleteMsg({ creditApplicationId, messageId })
      setLocalMessages((prev) => prev.filter((m) => m.id !== messageId))
    },
    [creditApplicationId, deleteMsg, isInputDisabled, setLocalMessages],
  )

  const handleEditMessage = useCallback(
    async (messageId: string, newContent: string) => {
      if (isInputDisabled || !creditApplicationId) return
      await editMsg({ creditApplicationId, messageId, content: newContent })
      setLocalMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, content: newContent, editedAt: new Date() } : m)),
      )
    },
    [creditApplicationId, editMsg, isInputDisabled, setLocalMessages],
  )

  const handleMarkUnreadMessage = useCallback(
    async (messageId: string) => {
      if (isInputDisabled || !creditApplicationId) return
      await markUnread({ creditApplicationId, messageId })
      setLocalMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, isRead: false, readAt: undefined, markedUnreadById: user.id } : m,
        ),
      )
    },
    [creditApplicationId, markUnread, user.id, isInputDisabled, setLocalMessages],
  )

  const handleSendMessage = useCallback(
    async (messageContent: string) => {
      if (!creditApplicationId || !financingProgramId) return
      await sendMsg({
        id: crypto.randomUUID(),
        content: messageContent,
        sentAt: new Date(),
        sentByName: user.name,
        sentById: user.id,
        merchantId: '',
        creditApplicationId,
        financingProgramId,
        isRead: false,
        type: CURRENT_USER_PARTICIPANT_TYPE,
      })
    },
    [creditApplicationId, financingProgramId, user.id, user.name, sendMsg],
  )

  const handleBack = useCallback(() => {
    navigate('/chat-dashboard')
  }, [navigate])

  const language = useMemo(() => {
    const lang = conversation?.merchantLanguage
    return lang === ELanguage.French ? 'FR' : 'EN'
  }, [conversation?.merchantLanguage])

  if (!featureFlags.useInstantMessage) return <Navigate to="/applications/browse" replace />

  if (isLoading) {
    return <ChatPageSkeletonLayout />
  }

  return (
    <FullScreenContainer>
      <Container maxWidth="lg" sx={{ py: 3, height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <ChatHeader
          province={conversation?.province}
          language={language}
          isAgent
          lock={lock}
          currentUserId={user.id}
          onBack={handleBack}
          onLock={handleAcquireLock}
          onUnlock={handleReleaseLock}
          onForceAcquire={handleForceAcquireLock}
          isLockLoading={acquireLockPending || releaseLockPending || forceAcquireLockPending}
        />

        <ContactInfoCard
          referenceNumber={conversation?.conversationNumber ?? ''}
          fullName={conversation?.fullName ?? ''}
          email={conversation?.email ?? ''}
          phone={conversation?.phone ?? ''}
          merchantName={conversation?.merchantName ?? ''}
          createdByName={conversation?.applicationCreatedByName ?? ''}
          isCancelled={isCancelled}
          onApplicationClick={() => {
            if (financingProgramId && creditApplicationId) {
              window.open(`/applications/${financingProgramId}/${creditApplicationId}/view`, '_blank')
            }
          }}
        />

        <Box sx={{ flex: 1, overflow: 'hidden', mb: 2 }}>
          <Chat
            messages={localMessages}
            systemMessage={conversation?.systemMessage}
            currentUserId={user.id}
            isAgent
            onDeleteMessage={handleDeleteMessage}
            onEditMessage={handleEditMessage}
            onMarkUnreadMessage={handleMarkUnreadMessage}
            isLockedByOther={isInputDisabled}
            isCancelled={isCancelled}
            chatPalette={chatPalette}
            getMessageVariant={getMessageVariant}
          />
        </Box>

        <Box>
          <QuickActionsBar
            onFormatClick={() => setFormatMode((prev) => !prev)}
            formatMode={formatMode}
            messageInputRef={messageInputRef}
            templates={templates}
            isLoadingTemplates={isLoadingTemplates}
            categories={TEMPLATE_CATEGORIES}
            fetchedTemplate={fetchedTemplate}
            isFetchingTemplate={isFetchingTemplate}
            fetchError={!!fetchError}
            onFetchTemplate={handleFetchTemplate}
          />
          <ChatMessageInputBar
            ref={messageInputRef}
            onSendMessage={handleSendMessage}
            formatMode={formatMode}
            disabled={isInputDisabled}
            lockedByName={isLockedByOther ? lock?.agentName : undefined}
            templates={templates}
            placeholders={placeholders}
            isInsertionLoading={isFetchingTemplate}
            insertionError={!!fetchError}
          />
        </Box>
      </Container>
    </FullScreenContainer>
  )
}

export default memo(ChatPage)
